import type React from 'react';
import { useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { selectLegibleElementColor } from 'reducers/config';
import { useAppDispatch, useAppSelector } from 'hooks';
import type { AttachmentStepText } from 'api/types';
import { useUpdateConversation } from 'components/App/hooks';
import { Typography } from '@forethought-technologies/forethought-elements';
import { onChangeHandler } from 'components/ZendeskAttachmentUploadStep/ZendeskAttachmentUploadStepHelpers';
import { setStepSent } from 'actions/workflows/workflows';

interface AddFileFormProps {
  acceptableFileTypes?: string;
  componentText: AttachmentStepText;
  fileSizeLimit: number;
  hasTooManyFiles: boolean;
  isError: boolean;
  numFiles: number | null;
  required: boolean;
  setFiles: React.Dispatch<React.SetStateAction<File[] | null>>;
}

const AddFileForm = ({
  acceptableFileTypes,
  componentText,
  fileSizeLimit,
  hasTooManyFiles,
  isError,
  numFiles,
  required,
  setFiles,
}: AddFileFormProps) => {
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const updateConversation = useUpdateConversation();
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const inputReference = useRef<HTMLInputElement>(null);

  return (
    <Box
      sx={{
        color: palette.colors.grey[600],
        display: 'flex',
        flexDirection: 'column',
        height: '80px',
        justifyContent: 'center',
        marginBottom: '10px',
        textAlign: 'center',
      }}
    >
      <Box display='flex' gap='5px' justifyContent='center'>
        <AddFileLabel
          backgroundColor={palette.colors.white}
          dir='auto'
          textColor={legibleElementColor}
        >
          {componentText.add_file}
          <input
            accept={acceptableFileTypes}
            className='visually-hidden'
            onChange={() => {
              onChangeHandler(inputReference, setFiles);
            }}
            ref={inputReference}
            type='file'
          />
        </AddFileLabel>
        {!required && (
          <>
            or
            <SkipUploadButton
              backgroundColor={palette.colors.white}
              color={palette.colors.grey[600]}
              dir='auto'
              onClick={() => {
                setFiles(null);
                dispatch(setStepSent({}));
                updateConversation({});
              }}
            >
              {componentText.skip_attachment}
            </SkipUploadButton>
          </>
        )}
      </Box>
      {isError && (
        <Typography color={palette.colors.red[500]} variant='font14Medium'>
          Pass file size limit - {fileSizeLimit}mb
        </Typography>
      )}
      {hasTooManyFiles && (
        <Typography color={palette.colors.red[500]} variant='font14Medium'>
          Pass file limit - {numFiles}
        </Typography>
      )}
    </Box>
  );
};

const AddFileLabel = styled('label')<{
  backgroundColor: string;
  textColor: string;
}>`
  border: none;
  outline: none;
  color: ${props => props.textColor};
  background: ${props => props.backgroundColor};
  cursor: pointer;
  text-decoration: underline;
  margin-right: 5px;

  :focus-within {
    outline: 2px solid var(--legible-element-color);
  }
`;

const SkipUploadButton = styled('button')<{
  backgroundColor: string;
  color: string;
}>`
  border: none;
  outline: none;
  text-decoration: underline;
  display: block;
  background: ${props => props.backgroundColor};
  color: ${props => props.color};
  cursor: pointer;
  padding: 0;
  margin-left: 5px;

  :focus-within {
    outline: 2px solid var(--legible-element-color);
  }
`;

export default AddFileForm;
